import React from "react";
import type { TypedWrappedFieldProps } from "redux-form";
import { Field, Fields } from "redux-form";

import {
  Box,
  Divider,
  GraterOrEqualZeroNumberFormat,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { FieldSectionPanel } from "@js/apps/jobs/forms/fields";
import { NumberField } from "@js/forms/fields";
import { isError, isTouched } from "@js/forms/utils";

import { LocationAndTimezoneSection } from "./location-timezone-section";
import { ProjectDurationField } from "./project-duration";

type ProjectDetailsSectionModuleProps = {
  contract_type: TypedWrappedFieldProps;
  deadline: TypedWrappedFieldProps;
  start_date: TypedWrappedFieldProps;
  expected_hours_per_week: TypedWrappedFieldProps;
  timezone_overlap: TypedWrappedFieldProps;
  location: TypedWrappedFieldProps;
  timezone: TypedWrappedFieldProps;
  isEditForm: boolean;
};

export const ProjectDetailsSectionModule = ({
  contract_type,
  deadline,
  start_date,
  expected_hours_per_week,
  timezone_overlap,
  location,
  timezone,
  isEditForm,
}: ProjectDetailsSectionModuleProps) => {
  const fields = [
    contract_type,
    expected_hours_per_week,
    timezone_overlap,
    deadline,
    start_date,
    location,
    timezone,
  ];
  const isAnyTouched = isTouched(fields);
  const isErrorInSection = isError(fields);
  const isErrorInDataPicker = isError([start_date, deadline]);
  const displayError =
    (isErrorInSection && isAnyTouched) || isErrorInDataPicker;

  return (
    <FieldSectionPanel
      title="Job Details"
      id="job-details"
      description="Length of job, working hours, location, etc."
      isError={displayError}
    >
      <Divider color="beige" sx={{ my: "20px" }} />
      <Fields
        names={["contract_type", "deadline", "start_date"]}
        component={ProjectDurationField}
        isEditForm={isEditForm}
      />
      <Divider color="beige" sx={{ my: 3 }} />

      <Typography
        component="label"
        id="expected-hours-label"
        htmlFor="expected-hours-per-week"
        sx={{ mb: "18px", display: "block" }}
      >
        How many hours per week will Talent work?
      </Typography>
      <Box>
        <Field
          name="expected_hours_per_week"
          id="expected-hours-per-week"
          component={NumberField}
          size="large"
          NumberFormat={GraterOrEqualZeroNumberFormat}
          normalize={Number}
          slotProps={{
            input: {
              style: {
                boxShadow: `var(--elevation-4)`,
                borderRadius: "9px",
                maxWidth: "168px",
              },
            },
          }}
        />
      </Box>

      <Fields
        names={["locations", "timezones", "timezone_overlap"]}
        component={LocationAndTimezoneSection}
      />
    </FieldSectionPanel>
  );
};
